/* RBAC, Audit, Templates, Users, LogoSelector */
const { useState: useStateR } = React;

// ============ RBAC: Users + Roles + Permission Matrix ============
function RBACPage() {
  const [tab, setTab] = useStateR('users');
  const [selectedRole, setSelectedRole] = useStateR('fin_mgr');
  const [showInvite, setShowInvite] = useStateR(false);

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Settings</span><I.ChevronRight s={10}/><span>Access Control</span></div>
          <h1>Users, Roles & Permissions</h1>
          <div className="subtitle">Fine-grained RBAC at page + action level. All changes are audited.</div>
        </div>
        <div className="page-header-actions">
          {window.can('users','create') && <button className="btn primary" onClick={() => setShowInvite(true)}><I.Plus/> Invite user</button>}
        </div>
      </div>
      <div className="tabs">
        {[['users', 'Users', QH_DATA.users.length], ['roles', 'Roles', QH_DATA.roles.length], ['permissions', 'Permission matrix']].map(([k, l, c]) => (
          <button key={k} className={`tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}{c != null && <span className="count">{c}</span>}</button>
        ))}
      </div>
      <div className="page-body">
        {tab === 'users' && <UsersTable/>}
        {tab === 'roles' && <RolesPanel selected={selectedRole} setSelected={setSelectedRole}/>}
        {tab === 'permissions' && <PermissionMatrix/>}
      </div>
      {showInvite && <UserModal onClose={() => setShowInvite(false)}/>}
    </>
  );
}

function UsersTable() {
  const [editing, setEditing] = useStateR(null);
  return (
    <>
    <div className="table-wrap">
      <table className="data">
        <thead><tr><th>Name</th><th>Username</th><th>Email</th><th>Role</th><th>Status</th><th>Last seen</th><th></th></tr></thead>
        <tbody>
          {QH_DATA.users.map(u => {
            const role = QH_DATA.roles.find(r => r.id === u.role);
            return (
              <tr key={u.id} className="clickable" onClick={() => window.can('users','edit') && setEditing(u)}>
                <td>
                  <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                    <span className="avatar">{u.name.split(' ').map(x => x[0]).slice(0, 2).join('')}</span>
                    {u.name}
                  </div>
                </td>
                <td className="font-mono text-xs">{u.username || '—'}</td>
                <td className="font-mono text-xs">{u.email}</td>
                <td>
                  <span className={`tag ${u.role === 'admin' ? 'accent' : ''}`}>
                    {u.role === 'admin' && <I.Shield2 s={10}/>}
                    {role?.name}
                  </span>
                </td>
                <td><span className={`tag ${u.status === 'active' ? 'pos' : ''}`}><span className="dot"/>{u.status}</span></td>
                <td className="font-mono text-xs muted">{u.lastSeen}</td>
                <td><button className="icon-btn" onClick={(e) => { e.stopPropagation(); if (window.can('users','edit')) setEditing(u); }}><I.MoreH/></button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
    {editing && <UserModal user={editing} onClose={() => setEditing(null)}/>}
    </>
  );
}

function RolesPanel({ selected, setSelected }) {
  const role = QH_DATA.roles.find(r => r.id === selected);
  // Local copy of permissions so the UI is responsive while typing — synced
  // back to QH_DATA on Save. Keyed by role.id so switching roles resets it.
  const [draft, setDraft] = useStateR(null);
  const [busy, setBusy] = useStateR(false);
  React.useEffect(() => {
    if (!role) { setDraft(null); return; }
    const base = role.id === 'admin'
      ? Object.fromEntries(QH_DATA.pages.map(p => [p.id, { read:true,create:true,edit:true,delete:true,whatsapp:true,email:true }]))
      : Object.fromEntries(QH_DATA.pages.map(p => [p.id, { ...(QH_DATA.permissions[role.id]?.[p.id] || {read:false,create:false,edit:false,delete:false,whatsapp:false,email:false}) }]));
    setDraft(base);
  }, [selected]);

  if (!role || !draft) return null;
  const editable = !role.locked && window.can('users','edit');

  const toggle = (pid, k) => {
    if (!editable) return;
    setDraft(d => ({ ...d, [pid]: { ...d[pid], [k]: !d[pid][k] } }));
  };
  const save = async () => {
    setBusy(true);
    try {
      const payload = { permissions: QH_DATA.pages.map(p => ({ pageId: p.id, ...draft[p.id] })) };
      await window.QH_API.put(`/roles/${role.id}/permissions`, payload);
      await window.QH_STORE.reload();
    } catch (e) { alert(e.message); } finally { setBusy(false); }
  };

  return (
    <div style={{display: 'grid', gridTemplateColumns: '300px 1fr', gap: 12}}>
      <div className="card" style={{padding: 0}}>
        <div className="card-header">Roles</div>
        <div style={{padding: 0}}>
          {QH_DATA.roles.map(r => (
            <button key={r.id} className={`nav-item ${selected === r.id ? 'active' : ''}`} onClick={() => setSelected(r.id)} style={{padding: '10px 14px', height: 'auto', alignItems: 'flex-start', flexDirection: 'column', gap: 2}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 8, width: '100%'}}>
                {r.id === 'admin' && <I.Shield2 s={13}/>}
                <span style={{fontWeight: 500}}>{r.name}</span>
                {r.locked && <I.Lock s={11}/>}
                <span style={{marginLeft: 'auto', fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--ink-3)'}}>{r.userCount}</span>
              </div>
              <div className="text-xs muted" style={{textAlign: 'left'}}>{r.desc}</div>
            </button>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="card-header">{role.name} · permissions <span className="actions">{role.locked ? <span className="tag"><I.Lock s={10}/> Cannot be modified</span> : (editable ? <button className="btn sm primary" disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save changes'}</button> : <span className="tag">Read-only</span>)}</span></div>
        <div className="card-body" style={{padding: 0}}>
          <div className="perm-grid">
            <div className="head">Page</div>
            <div className="head">Read</div>
            <div className="head">Create</div>
            <div className="head">Edit</div>
            <div className="head">Delete</div>
            <div className="head"><I.MessageSquare s={11}/></div>
            <div className="head"><I.Mail s={11}/></div>
            {QH_DATA.pages.map(p => {
              const perms = draft[p.id] || {};
              return (
                <React.Fragment key={p.id}>
                  <div className="row-head">{p.label}</div>
                  {['read', 'create', 'edit', 'delete', 'whatsapp', 'email'].map(k => (
                    <div key={k} className="perm-cell" onClick={() => toggle(p.id, k)} style={{cursor: editable ? 'pointer' : 'default'}}>
                      <span className={`check ${perms[k] ? 'on' : ''} ${editable ? '' : 'disabled'}`}/>
                    </div>
                  ))}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function PermissionMatrix() {
  const actions = ['read', 'create', 'edit', 'delete', 'whatsapp', 'email'];
  return (
    <div className="card">
      <div className="card-header">Cross-role permission matrix <span className="actions"><span className="tag info">{QH_DATA.roles.length} roles × {QH_DATA.pages.length} pages × {actions.length} actions</span></span></div>
      <div className="card-body" style={{overflowX: 'auto', padding: 0}}>
        <table className="data" style={{minWidth: '100%'}}>
          <thead>
            <tr>
              <th rowSpan="2" style={{position: 'sticky', left: 0, zIndex: 2, minWidth: 180, background: 'var(--bg-sunken)'}}>Page</th>
              {QH_DATA.roles.map(r => <th key={r.id} colSpan={actions.length} style={{textAlign: 'center', borderLeft: '1px solid var(--line)'}}>{r.name}</th>)}
            </tr>
            <tr>
              {QH_DATA.roles.flatMap(r => actions.map(a => (
                <th key={r.id + a} style={{textAlign: 'center', fontSize: 9, borderLeft: a === 'read' ? '1px solid var(--line)' : 'none', padding: '6px 4px'}}>
                  {a === 'whatsapp' ? 'W' : a === 'email' ? 'E' : a[0].toUpperCase()}
                </th>
              )))}
            </tr>
          </thead>
          <tbody>
            {QH_DATA.pages.map(p => (
              <tr key={p.id}>
                <td style={{position: 'sticky', left: 0, background: 'var(--bg-elev)', zIndex: 1, fontWeight: 500}}>{p.label}</td>
                {QH_DATA.roles.flatMap(r => {
                  const perms = r.id === 'admin' ? Object.fromEntries(actions.map(a => [a, true])) : (QH_DATA.permissions[r.id]?.[p.id] || {});
                  return actions.map(a => (
                    <td key={r.id + p.id + a} style={{textAlign: 'center', padding: 4, borderLeft: a === 'read' ? '1px solid var(--line)' : 'none'}}>
                      {perms[a] ? <span style={{display: 'inline-block', width: 8, height: 8, background: 'var(--accent)', borderRadius: 2}}/> : <span style={{color: 'var(--ink-4)'}}>·</span>}
                    </td>
                  ));
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ Audit ============
function AuditPage() {
  const [filter, setFilter] = useStateR('all');
  const filtered = filter === 'all' ? QH_DATA.auditLog : QH_DATA.auditLog.filter(a => a.action.startsWith(filter));
  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Compliance</span></div>
          <h1>Audit Log</h1>
          <div className="subtitle">All system actions are logged with user, role, timestamp & detail. Immutable record.</div>
        </div>
        <div className="page-header-actions">
          <button className="btn"><I.Filter/> Date range</button>
          <button className="btn primary"><I.Download/> Export CSV</button>
        </div>
      </div>
      <div className="toolbar">
        {[['all', 'All'], ['login', 'Auth'], ['transaction', 'Transactions'], ['scheme', 'Schemes'], ['finance', 'Finance'], ['user', 'Users'], ['role', 'RBAC'], ['notification', 'Notifications']].map(([k, l]) => (
          <button key={k} onClick={() => setFilter(k)} className={`btn sm ${filter === k ? 'accent' : 'ghost'}`}>{l}</button>
        ))}
        <div className="grow"/>
        <input className="input" style={{height: 28, width: 220}} placeholder="Search log…"/>
      </div>
      <div className="page-body">
        <div className="table-wrap">
          <table className="data">
            <thead><tr><th>Timestamp</th><th>User</th><th>Role</th><th>Action</th><th>Target</th><th>Detail</th></tr></thead>
            <tbody>
              {filtered.map((a, i) => (
                <tr key={i}>
                  <td className="font-mono text-xs">{a.ts}</td>
                  <td>
                    <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                      <span className="avatar" style={{width: 22, height: 22, fontSize: 9}}>{a.user.split(' ').map(x => x[0]).slice(0, 2).join('')}</span>
                      <span style={{fontSize: 12}}>{a.user}</span>
                    </div>
                  </td>
                  <td><span className="tag">{a.role}</span></td>
                  <td>
                    <span style={{display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'var(--font-mono)', fontSize: 11}}>
                      <span style={{color: 'var(--ink-3)'}}><ActIcon action={a.action}/></span>
                      {a.action}
                    </span>
                  </td>
                  <td className="font-mono text-xs">{a.target}</td>
                  <td className="text-xs muted">{a.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

// ============ Notification Templates ============
function TemplatesPage() {
  const [selected, setSelected] = useStateR(QH_DATA.templates[0]?.id || '');
  const [showCreate, setShowCreate] = useStateR(false);
  const [editing, setEditing] = useStateR(null);
  const tpl = QH_DATA.templates.find(t => t.id === selected);
  if (!tpl) return <div className="page-body">No templates yet.</div>;

  const remove = async () => {
    if (!window.confirm(`Delete template "${tpl.name}"?`)) return;
    try { await window.QH_API.delete(`/templates/${tpl.id}`); await window.QH_STORE.reload(); setSelected(QH_DATA.templates[0]?.id || ''); }
    catch (e) { alert(e.message); }
  };

  return (
    <>
      <div className="page-header">
        <div>
          <div className="breadcrumb"><span>Settings</span><I.ChevronRight s={10}/><span>Notifications</span></div>
          <h1>Notification Templates</h1>
          <div className="subtitle">WhatsApp & Email message templates. Sending requires explicit per-role permission.</div>
        </div>
        <div className="page-header-actions">
          {window.can('templates','create') && <button className="btn primary" onClick={() => setShowCreate(true)}><I.Plus/> New template</button>}
        </div>
      </div>
      {showCreate && <TemplateModal onClose={() => setShowCreate(false)}/>}
      {editing && <TemplateModal template={editing} onClose={() => setEditing(null)}/>}
      <div className="page-body" style={{display: 'grid', gridTemplateColumns: '320px 1fr', gap: 12}}>
        <div className="card" style={{padding: 0, alignSelf: 'flex-start'}}>
          <div className="card-header">Templates</div>
          {QH_DATA.templates.map(t => (
            <button key={t.id} className={`nav-item ${selected === t.id ? 'active' : ''}`} onClick={() => setSelected(t.id)} style={{padding: '10px 14px', height: 'auto', alignItems: 'flex-start', flexDirection: 'column', gap: 4}}>
              <div style={{display: 'flex', alignItems: 'center', gap: 6, width: '100%'}}>
                <span style={{fontWeight: 500, fontSize: 13}}>{t.name}</span>
                <span style={{marginLeft: 'auto', display: 'flex', gap: 4}}>
                  {(t.channel === 'whatsapp' || t.channel === 'both') && <span className="tag info" style={{padding: '0 4px'}}><I.MessageSquare s={9}/></span>}
                  {(t.channel === 'email' || t.channel === 'both') && <span className="tag info" style={{padding: '0 4px'}}><I.Mail s={9}/></span>}
                </span>
              </div>
              <div className="text-xs muted" style={{textAlign: 'left'}}>{t.scope}</div>
            </button>
          ))}
        </div>
        {/* key={tpl.id} forces a remount when the user picks a different */}
        {/* template in the sidebar. Without it React reuses the same input */}
        {/* DOM nodes and `defaultValue` is ignored on subsequent renders, */}
        {/* so the Message body keeps showing the previous template's text. */}
        <div className="card" key={tpl.id}>
          <div className="card-header">{tpl.name} <span className="actions"><span className="tag info">{tpl.channel}</span>{window.can('templates','edit') && <button className="btn sm primary" onClick={() => setEditing(tpl)}>Edit</button>}{window.can('templates','delete') && <button className="btn sm danger" onClick={remove}>Delete</button>}</span></div>
          <div className="card-body" style={{display: 'flex', flexDirection: 'column', gap: 12}}>
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
              <div className="field"><label>Template name</label><input className="input" defaultValue={tpl.name} readOnly/></div>
              <div className="field"><label>Channel</label><select className="select" defaultValue={tpl.channel} disabled><option value="whatsapp">WhatsApp</option><option value="email">Email</option><option value="both">Both</option></select></div>
              <div className="field" style={{gridColumn: '1 / -1'}}><label>Scope</label><input className="input" defaultValue={tpl.scope} readOnly/></div>
            </div>
            <div className="field">
              <label>Message body</label>
              <textarea className="textarea" rows={5} defaultValue={tpl.body} readOnly/>
              <div className="hint">Variables: {`{{member}}, {{amount}}, {{scheme}}, {{date}}, {{txn_id}}, {{maturity}}, {{member_balance}}, {{applicant}}, {{app_id}}, {{cheque}}, {{reason}}`}. Use the Edit button above to modify.</div>
            </div>
            <div className="divider"/>
            <div>
              <div className="text-xs uppercase muted" style={{marginBottom: 8}}>Preview</div>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12}}>
                <div style={{background: '#0F1A1F', borderRadius: 12, padding: 14, color: 'white', fontSize: 13, fontFamily: 'system-ui, sans-serif', maxWidth: 280}}>
                  <div style={{display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, paddingBottom: 8, borderBottom: '1px solid rgba(255,255,255,0.1)'}}>
                    <div style={{width: 24, height: 24, background: '#25D366', borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 11}}>QH</div>
                    <div><div style={{fontSize: 12, fontWeight: 500}}>QH Management</div><div style={{fontSize: 9, opacity: 0.6}}>WhatsApp Business</div></div>
                  </div>
                  <div style={{background: 'rgba(37,211,102,0.15)', padding: 10, borderRadius: 8, lineHeight: 1.5}}>
                    {window.renderTemplate(tpl.body, { member: 'Sara Bu Hamood', amount: '1,200.000', scheme: 'Riyadh Heritage Pool', date: '27 Apr 2026', txn_id: 'TXN-2839', maturity: '27 Apr 2028', member_balance: '8,450.000', applicant: 'Sara Bu Hamood', app_id: 'PF-102', cheque: 'CHQ-558920', reason: '—' })}
                  </div>
                </div>
                <div className="card" style={{margin: 0, borderRadius: 6}}>
                  <div style={{padding: 10, borderBottom: '1px solid var(--line)', fontSize: 12}}>
                    <div className="muted text-xs">From: notifications@qh.bh</div>
                    <div className="muted text-xs">To: sara.buhamood@example.com</div>
                    <div style={{fontWeight: 600, marginTop: 4}}>Re: {tpl.name}</div>
                  </div>
                  <div style={{padding: 12, fontSize: 12, lineHeight: 1.6}}>
                    {window.renderTemplate(tpl.body, { member: 'Sara Bu Hamood', amount: '1,200.000', scheme: 'Riyadh Heritage Pool', date: '27 Apr 2026', txn_id: 'TXN-2839', maturity: '27 Apr 2028', member_balance: '8,450.000', applicant: 'Sara Bu Hamood', app_id: 'PF-102', cheque: 'CHQ-558920', reason: '—' })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

// ============ Logo Selector card (in tweaks panel) ============
function LogoSelectorPanel({ palette, setPalette, logoVariant, setLogoVariant }) {
  const opts = [
    { variant: 'geometric', palette: 'vault', name: 'Vault', sub: 'Navy + Gold · classic' },
    { variant: 'abstract', palette: 'pulse', name: 'Pulse', sub: 'Indigo + Violet · fintech' },
    { variant: 'classic', palette: 'grove', name: 'Grove', sub: 'Forest + Cream · heritage' },
  ];
  const active = `${logoVariant}-${palette}`;
  return (
    <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
      <div className="logo-cards">
        {opts.map(o => {
          const a = `${o.variant}-${o.palette}`;
          return (
            <div key={a} className={`logo-card ${active === a ? 'active' : ''}`} onClick={() => { setLogoVariant(o.variant); setPalette(o.palette); }}>
              <div className="preview" style={{
                background: o.palette === 'vault' ? '#0B1F3A' : o.palette === 'pulse' ? '#1E1B4B' : '#0F3D2E',
                color: 'white',
              }}>
                <QHLogo variant={o.variant} palette={o.palette} size={26} showWordmark={false}/>
              </div>
              <div>
                <div style={{fontWeight: 600, fontSize: 13}}>{o.name}</div>
                <div className="text-xs muted">{o.sub}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

window.RBACPage = RBACPage;
window.AuditPage = AuditPage;
window.TemplatesPage = TemplatesPage;
window.LogoSelectorPanel = LogoSelectorPanel;
